/**
 * Typed client for jobs (WIRE-03; Time & Materials additions below).
 *
 * Routes (beam-backend domainRouter.ts):
 *   GET  /api/jobs                       → JobRow[]  (org-scoped list)
 *   GET  /api/jobs/:id/profitability     → profitability (see jobs/[id].tsx)
 *   GET  /api/labor-rates                → LaborRateDto[]
 *   GET  /api/jobs/:id/actuals           → JobActualsDto
 *   POST /api/jobs/:id/time-entries      { CreateTimeEntryRequest }
 *   POST /api/jobs/:id/expenses          { CreateManualExpenseRequest }
 *   POST /api/jobs/:id/invoice-from-actuals → CreateInvoiceFromActualsResponse
 *
 * Endpoints return a camelCase list DTO except the T&M additions, which mirror the raw
 * domain shape (snake_case) and are validated with the @beam/api-types zod schemas so the
 * app fails loudly on contract drift. Bearer token via client. NEVER imports @smartbox/*
 * (mobile invariant 1).
 */
import { z } from 'zod';
import { get, post } from './client';
import {
  LaborRateDto,
  JobActualsDto,
  CreateInvoiceFromActualsResponse,
} from '@beam/api-types';
import type {
  LaborRateDto as LaborRateDtoType,
  JobActualsDto as JobActualsDtoType,
  CreateTimeEntryRequest,
  CreateManualExpenseRequest,
  CreateInvoiceFromActualsResponse as CreateInvoiceFromActualsResponseType,
} from '@beam/api-types';

export interface JobRow {
  id: string;
  jobNumber: string | null;
  name: string | null;
  status: string;
  customerId: string;
  customerName: string | null;
  contractedAmount: string | null;
  scheduledStart: string | null;
  createdAt: string;
}

/** List the org's jobs (most recent first). */
export async function listJobs(): Promise<JobRow[]> {
  return get<JobRow[]>('/api/jobs');
}

// ---------------------------------------------------------------------------
// Time & Materials (accrue labor + materials against a job, then bill from actuals)
// ---------------------------------------------------------------------------

/** List the org's labor rates (fed to the "Log hours" rate picker). */
export async function listLaborRates(): Promise<LaborRateDtoType[]> {
  return z.array(LaborRateDto).parse(await get<unknown>('/api/labor-rates'));
}

/** Fetch the job's unbilled labor + materials actuals (the T&M invoice preview). */
export async function getJobActuals(jobId: string): Promise<JobActualsDtoType> {
  return JobActualsDto.parse(await get<unknown>(`/api/jobs/${jobId}/actuals`));
}

/** Log worked hours against the job. */
export async function createTimeEntry(jobId: string, body: CreateTimeEntryRequest): Promise<void> {
  await post<unknown>(`/api/jobs/${jobId}/time-entries`, body);
}

/** Log a manual material/expense line against the job. */
export async function createManualExpense(jobId: string, body: CreateManualExpenseRequest): Promise<void> {
  await post<unknown>(`/api/jobs/${jobId}/expenses`, body);
}

/** Bill an invoice from the job's unbilled actuals. 409s when there is nothing to bill or
 * some logged labor has no rate (see JobActualsDto.hasUnratedLabor). */
export async function createInvoiceFromActuals(jobId: string): Promise<CreateInvoiceFromActualsResponseType> {
  return CreateInvoiceFromActualsResponse.parse(
    await post<unknown>(`/api/jobs/${jobId}/invoice-from-actuals`),
  );
}
